<template>
    <div class="card b-all shadow-sm rounded">
        <div class="card-body rounded">
            <div class="table-filters">
                <div class="row justify-content-between">
                    <div class="col-md-12">
                        <div class="form-inline flex flex-wrap justify-center md:justify-start items-center gap-2">
                            <div v-if="!showBulkActions" class="btn-group">
                                <button type="button" class="btn btn-outline-primary dropdown-toggle"
                                        data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                                    {{ mutableSelectedMailbox ? mutableSelectedMailbox.name : "All Mailboxes" }}
                                </button>
                                <div class="dropdown-menu">
                                    <a href="#" v-for="mailbox in app.state.user.mailboxes" class="dropdown-item"
                                       @click.prevent="filterMailbox(mailbox)">
                                        {{ mailbox.email }}
                                    </a>
                                    <div class="dropdown-divider"></div>
                                    <a href="#" class="dropdown-item" @click.prevent="filterMailbox()">All Mailboxes</a>
                                </div>
                            </div>
                            <div v-if="!showBulkActions" class="input-group">
                                <input type="text"
                                       v-model="searchParams.filter"
                                       @keyup.enter="reloadTable"
                                       placeholder="Search mail"
                                       class="form-control h-9">
                                <div class="input-group-append">
                                    <button class="btn btn-primary" type="button" @click="reloadTable">
                                        Search
                                    </button>
                                </div>
                            </div>

                            <div title="Filter by the date of the last email received in an email thread"
                                 v-if="hasFeature('release_email_date_picker') && folder !== 'drafts'"
                                 class="d-flex flex-column mr-2 font-normal"
                                 role="group">
                                <el-date-picker
                                    v-model="searchParams.last_message_received_date"
                                    value-format="yyyy-MM-dd"
                                    type="daterange"
                                    range-separator="-"
                                    :clearable="false"
                                    :picker-options="datePickerOptions"
                                    @change="reloadTable()"
                                    start-placeholder="Start date"
                                    end-placeholder="End date">
                                </el-date-picker>
                            </div>

                            <div v-if="!showBulkActions && folder !== 'sent'"
                                 class="d-flex flex-column mr-2 font-normal" role="group">
                                <el-checkbox class="flex-grow-1 justify-content-start ml-0 font-normal"
                                             v-model="searchParams.unread"
                                             @change="reloadTable()">
                                    Unread
                                </el-checkbox>
                            </div>

                            <div v-if="!showBulkActions && folder === 'sent'"
                                 class="d-flex flex-column mr-2 font-normal" role="group">
                                <el-checkbox class="flex-grow-1 justify-content-start ml-0 font-normal"
                                             v-model="searchParams.failed"
                                             @change="reloadTable()">
                                    Failed
                                </el-checkbox>
                            </div>

                            <div v-if="showBulkActions" class="flex items-center space-x-2">
                                <button v-if="folder === 'inbox'" type="button" class="btn btn-outline-primary"
                                        @click="markThreadsAsDone()">
                                    Move to Done
                                </button>
                                <button v-if="folder === 'done'" type="button" class="btn btn-outline-primary"
                                        @click="moveThreadsToInbox()">
                                    Move to Inbox
                                </button>
                                <button type="button" class="btn btn-outline-primary"
                                        v-if="['inbox', 'done'].includes(folder)"
                                        @click="toggleThreadReadStatus(true)">
                                    Mark as Unread
                                </button>
                                <button type="button" class="btn btn-outline-primary"
                                        v-if="['inbox', 'done'].includes(folder)"
                                        @click="toggleThreadReadStatus(false)">
                                    Mark as Read
                                </button>
                                <button v-if="['inbox', 'done', 'drafts', 'sent'].includes(folder)" type="button"
                                        class="btn btn-outline-danger" @click="deleteSelectedThreads()">
                                    Delete
                                </button>
                                <button v-if="folder === 'deleted'" type="button" class="btn btn-outline-danger"
                                        @click="restoreSelectedThreads()">
                                    Restore
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="inbox-center table-responsive" :class="{'table-loading' : isLoadingTableData}">
                <vuetable ref="vuetable"
                          api-url="/emails/all"
                          :fields="columns"
                          class="table"
                          :css="css.table"
                          pagination-path=""
                          :per-page="20"
                          :append-params="searchParams"
                          :no-data-template="emptyListLabel"
                          @vuetable:loading="onLoading"
                          @vuetable:loaded="onLoaded"
                          @vuetable:checkbox-toggled="checkboxToggled"
                          @vuetable:checkbox-toggled-all="checkboxToggled"
                          @vuetable:pagination-data="onPaginationData">

                    <span slot="email" slot-scope="props" class="hide-while-loading"
                          :class="{'unread':props.rowData.unread}" v-html="emailValue(props.rowData)">
                    </span>
                    <span slot="subject" slot-scope="props" class="hide-while-loading email-subject-width cursor-pointer hover:text-blue-500"
                          :class="{'unread':props.rowData.unread}"
                          @click="onActionClick('view-thread', props.rowData)">
                        <span v-if="showEmailFailed(props.rowData)" class="label label-sm label-light-danger">Failed</span> {{ props.rowData.subject }}
                    </span>
                    <span slot="updated_at" slot-scope="props" class="hide-while-loading"
                          :class="{'unread':props.rowData.unread}">
                        {{ emailUpdatedTimeValue(props.rowData) | datetime }}
                    </span>

                    <template slot="actions" slot-scope="props">
                        <div class="relative inline-block text-left">
                            <div>
                                <button type="button" @click="toggleDropdownMenuForRow(props.rowData)"
                                        class="flex items-center text-blue-400 hover:text-blue-600 focus:outline-none focus:ring-0 focus:ring-transparent"
                                        id="menu-button" aria-expanded="true" aria-haspopup="true" title="Open options menu">
                                    <span class="sr-only">Open options</span>
                                    <icon-vertical-dots class="w-4 h-4"></icon-vertical-dots>
                                </button>
                            </div>

                            <transition enter-active-class="transition ease-out duration-100" enter-from-class="transform opacity-0 scale-95"
                                        enter-to-class="transform opacity-100 scale-100" leave-active-class="transition ease-in duration-75"
                                        leave-from-class="transform opacity-100 scale-100" leave-to-class="transform opacity-0 scale-95">
                                <div class="origin-top-right absolute z-50 right-0 mt-2 rounded-md bg-white focus:outline-none"
                                     role="menu" aria-orientation="vertical" aria-labelledby="menu-button" tabindex="-1">
                                    <div v-click-outside="closeRowMenu"
                                         v-if="dropdownOpenForRow(props.rowData)" class="py-1 z-10 shadow-md rounded-md border border-top border-gray-100" role="none">
                                        <a class="text-gray-700 block px-4 py-2 text-sm bg-white hover:bg-gray-100" role="menuitem" tabindex="-1" id="menu-item-1"
                                           href="#"
                                           v-if="['inbox', 'done'].includes(folder) && props.rowData.unread"
                                           @click="toggleThreadReadStatus(false, props.rowData.id)">
                                            Mark as Read
                                        </a>
                                        <a class="text-gray-700 block px-4 py-2 text-sm bg-white hover:bg-gray-100" role="menuitem" tabindex="-1" id="menu-item-2"
                                           href="#"
                                           v-if="['inbox', 'done'].includes(folder) && !props.rowData.unread"
                                           @click="toggleThreadReadStatus(true, props.rowData.id)">
                                            Mark as Unread
                                        </a>
                                        <a class="text-gray-700 block px-4 py-2 text-sm bg-white hover:bg-gray-100" role="menuitem" tabindex="-1" id="menu-item-0"
                                           href="#"
                                           v-if="folder === 'inbox'"
                                           @click="onActionClick('move-to-done', props.rowData)">
                                            Move To Done
                                        </a>
                                        <a class="text-gray-700 block px-4 py-2 text-sm bg-white hover:bg-gray-100" role="menuitem" tabindex="-1" id="menu-item-3"
                                           href="#"
                                           @click="onActionClick('view-thread', props.rowData)">
                                            View Thread
                                        </a>
                                    </div>
                                </div>
                            </transition>
                        </div>
                    </template>
                </vuetable>

                <vuetable-pagination-info ref="paginationInfo" no-data-template=""></vuetable-pagination-info>

                <div class="text-right">
                    <vuetable-pagination ref="pagination" @vuetable-pagination:change-page="onChangePage"></vuetable-pagination>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import dayjs from "dayjs";

export default {
    name: "InboxList",

    mixins: [require("../../mixins/vuetable-css-mixin")],

    props: {
        folder: {
            required: true,
            type: String,
            description: "Current Folder"
        },
        unreadFilter: {
            required: false,
            type: Boolean,
            description: "Set the unread filter"
        }
    },

    data () {
        return {
            searchParams: {
                filter: "",
                folder: this.folder,
                unread: false,
                failed: false,
                mailbox: null,
                last_message_received_date: [dayjs().subtract(1, "year").format("YYYY-MM-DD"), dayjs().format("YYYY-MM-DD")],
            },
            showBulkActions: false,
            isLoadingTableData: false,
            datePickerOptions: {
                disabledDate: this.disableDates,
                shortcuts: [
                    {
                        text: "Today",
                        onClick (picker) {
                            const end = new Date;
                            const start = new Date;
                            picker.$emit("pick", [start, end]);
                        }
                    },
                    {
                        text: "Last week",
                        onClick (picker) {
                            const end = new Date();
                            const start = dayjs().subtract(1, "week").toDate();
                            picker.$emit("pick", [start, end]);
                        }
                    },
                    {
                        text: "Last 3 months",
                        onClick (picker) {
                            const end = new Date();
                            const start = dayjs().subtract(3, "months").toDate();
                            picker.$emit("pick", [start, end]);
                        }
                    }, {
                        text: "Last 6 months",
                        onClick (picker) {
                            const end = new Date();
                            const start = dayjs().subtract(6, "months").toDate();
                            picker.$emit("pick", [start, end]);
                        }
                    },
                    {
                        text: "Last 1 year",
                        onClick (picker) {
                            const end = new Date();
                            const start = dayjs().subtract(1, "year").toDate();
                            picker.$emit("pick", [start, end]);
                        }
                    }, {
                        text: "Last 2 years",
                        onClick (picker) {
                            const end = new Date();
                            const start = dayjs().subtract(2, "years").toDate();
                            picker.$emit("pick", [start, end]);
                        }
                    }, {
                        text: "Last 3 years",
                        onClick (picker) {
                            const end = new Date();
                            const start = dayjs().subtract(3, "years").toDate();
                            picker.$emit("pick", [start, end]);
                        }
                    }]
            },
            columns: [{
                name: "__checkbox",
                titleClass: "center aligned column-checkbox",
                dataClass: "center aligned column-checkbox"
            }, {
                name: "__slot:email",
                title: "From",
                dataClass: "text-nowrap email-from-column",
                titleClass: "email-from-column-header"
            }, {
                name: "__slot:subject",
                title: "Subject",
                dataClass: "email-subject-column",
                titleClass: "email-subject-column whitespace-nowrap cursor-pointer"
            }, {
                name: "__slot:updated_at",
                title: "Date",
                titleClass: "email-updated-at-column"
            }, {
                name: "__slot:actions",
                title: "",
                titleClass: "text-right email-actions-column",
                dataClass: "text-right"
            }],

            menuOpenFor: null
        };
    },

    computed: {
        ...mapGetters(["mutableSelectedMailbox"]),
        emptyListLabel () {
            if (this.isLoadingTableData) {
                return "Loading...";
            }
            switch (this.folder) {
                case "inbox":
                    return "No emails in inbox!";
                case "done":
                    return "Mark emails as done to move them here.";
                case "drafts":
                    return "No draft emails.";
                case "sent":
                    return "No sent emails.";
                case "deleted":
                    return "No deleted emails.";
                default:
                    return "";
            }
        },

        yesterday () {
            return dayjs().subtract(1, "day");
        },
    },

    watch: {
        folder (folder) {
            this.configureMailFolder(folder)

            this.reloadTable();
        },

        unreadFilter (value) {
            this.searchParams.unread = value;
            this.reloadTable();
        }
    },

    created () {
        this.searchParams.mailbox = this.mutableSelectedMailbox ? this.mutableSelectedMailbox.id : null;
    },

    mounted () {
        if(this.folder !== "inbox") {
            this.configureMailFolder(this.folder);
        }
        this.css.table.tableClass = "table table-fixed";
    },

    methods: {
        configureMailFolder(folder){
            this.searchParams.folder = folder;
            let emailColumn = this.columns.find(col => col.name === "__slot:email");
            if (emailColumn) {
                if (folder === "drafts" || folder === "sent") {
                    emailColumn.title = "To";
                } else if (folder === "deleted") {
                    emailColumn.title = "Customer";
                } else {
                    emailColumn.title = "From";
                }

                if (this.$refs.vuetable) {
                    this.$refs.vuetable.normalizeFields();
                }
            }

        },
        dropdownOpenForRow (propData) {
            return this.menuOpenFor && this.menuOpenFor === propData.id;
        },

        closeRowMenu () {
            this.menuOpenFor = null;
        },

        toggleDropdownMenuForRow (propData) {
            if (this.menuOpenFor) {
                this.menuOpenFor = null;
            } else {
                this.menuOpenFor = propData.id;
            }
        },

        filterMailbox (mailbox) {
            this.$store.dispatch("UPDATE_SELECTED_MAILBOX", mailbox);
            let self = this;
            this.$nextTick(() => self.reloadTable());
        },

        moveThreadsToInbox () {
            this.moveThreadsTo("inbox");
        },

        markThreadsAsDone () {
            this.moveThreadsTo("done");
        },

        deleteSelectedThreads () {
            let title = this.$refs.vuetable.selectedTo.length === 1 ? "Delete Email" : "Delete " + this.$refs.vuetable.selectedTo.length + " emails";
            let message = this.$refs.vuetable.selectedTo.length === 1 ? "Are you sure you want to delete this email?" : "Are you sure you want to delete these emails?";

            let self = this;
            swal({
                title: title,
                text: message,
                showCancelButton: true
            }).then((result) => {
                if (result.value) {
                    self.moveThreadsTo("deleted");
                }
            });
        },

        emailValue (data) {
            let email = "";

            if (!data.last_email) {
                return email;
            }

            if (this.folder === "drafts" || this.folder === "sent" || data.last_email.type === "sent") {
                email = data.last_email.to_name !== null ? data.last_email.to_name : data.last_email.to;
            } else {
                email = data.last_email.from_name !== null ? data.last_email.from_name : data.last_email.from;
            }

            if (data.emails_count > 1 && data.emails_count <= 25) {
                email += " (" + data.emails_count + ")";
            } else if (data.emails_count > 25) {
                email += " (25+)";
            }

            return email;
        },

        restoreSelectedThreads () {
            this.moveThreadsTo("restored");
        },

        moveThreadsTo (folder) {
            this.postThreadsFolderUpdate(this.$refs.vuetable.selectedTo, folder);
        },

        postThreadsFolderUpdate (threads, folder) {
            App.post("emails/threads/move", new AppForm({
                threads: threads,
                folder: folder
            })).then((response) => {
                toastr.success(response);
                this.reloadTable();
            });
        },

        moveThread (thread, folder) {
            this.postThreadsFolderUpdate([thread], folder);
        },

        reloadTable () {
            this.searchParams.mailbox = this.mutableSelectedMailbox ? this.mutableSelectedMailbox.id : null;

            this.$nextTick(() => this.$refs.vuetable.reload());

            if (this.$refs.vuetable) {
                this.$refs.vuetable.selectedTo = [];
                // if we reload but the page is empty. refresh instead (1 counts as empty because the tableData will be updated on refresh)
                if (this.$refs.vuetable.tableData && this.$refs.vuetable.tableData.length === 1) {
                    this.$refs.vuetable.refresh();
                }
            }
            this.checkboxToggled();
            this.$emit("update-email-counters");
        },

        showEmailFailed (thread) {
            return !!(this.folder === "sent" && thread.last_email &&
                ((thread.last_email.failed_event && !thread.last_email.delivered_event) || thread.last_email.sent_status === "failed"));
        },

        onActionClick (action, data) {
            switch (action) {
                case "move-to-done":
                    this.moveThread(data.id, "done");
                    break;
                case "view-thread":
                    this.$emit("load-thread", data.id);
                    this.$emit("view-message", true);
                    break;
            }
        },

        toggleThreadReadStatus (markAsUnread = true, threadId = null) {
            let threadIds = [];

            if (threadId) {
                // Single thread selected via dropdown. Add to list of threads to toggle.
                threadIds.push(threadId);

                //Close the menu for the selected thread.
                this.toggleDropdownMenuForRow(threadId);
            } else {
                // Bulk selection of threads. Get selected by accessing Vuetable's selectedTo data property
                threadIds = this.$refs.vuetable.selectedTo;
            }

            App.post("/emails/threads/toggle-read-status", new AppForm({
                thread_ids: threadIds,
                mark_as_unread: markAsUnread,
            }))
            .then((response) => {
                toastr.success(response);
                this.reloadTable();
            })
            .catch((error) =>
                toastr.error("Failed marking email as unread! " + error.message)
            );
        },

        emailUpdatedTimeValue (data) {
            if (!data.last_email) {
                return data.updated_at;
            }

            if (this.folder !== "drafts") {
                return data.last_email.sent_at !== null ? data.last_email.sent_at : data.updated_at;
            }

            return data.last_email.updated_at;
        },

        onPaginationData (paginationData) {
            this.$refs.pagination.setPaginationData(paginationData);
            this.$refs.paginationInfo.setPaginationData(paginationData);
        },

        onChangePage (page) {
            this.$refs.vuetable.changePage(page);
        },

        onLoading () {
            this.isLoadingTableData = true;
        },

        onLoaded () {
            this.isLoadingTableData = false;
            this.$emit("stick");
        },

        checkboxToggled () {
            this.showBulkActions = this.$refs.vuetable !== undefined ? this.$refs.vuetable.selectedTo.length > 0 : false;
        },
        disableDates (date) {
            // Date range has to be before today
            return date > this.yesterday;
        },
    },
};
</script>
